import { type FloatingContext, FloatingFocusManager, FloatingList, FloatingPortal, type Placement, type UseFloatingReturn } from '@floating-ui/react'
import { AnimatePresence, motion } from 'motion/react'
import { type ComponentProps, type ReactNode, useId, useRef, useState } from 'react'

import { astraTwMerge } from '../Utilities/astraTwMerge'
import { FLOATING_MOTION_PROPS, TRANSFORM_ORIGIN_BY_PLACEMENT } from '../Utilities/floatingMotion'
import type { DropdownList } from './dropdownFloating'
import { type DropdownHtmlProps, DropdownMenuContext, NO_SUBMENU, type OpenSubmenu, OpenSubmenuContext } from './DropdownMenuContext'
import { type DropdownMenuSearch, DropdownPanelBody } from './DropdownSearch'
import { DROPDOWN_PANEL_CLASS, DROPDOWN_PANEL_PADDING_CLASS, DROPDOWN_SEARCH_PANEL_CLASS } from './dropdownStyles'

/**
 * The panel is a motion element, and motion gives these four handlers its own animation and drag
 * signatures. Forwarding the DOM versions would collide, and a menu panel has no use for them.
 */
type PanelDivProps = Omit<ComponentProps<'div'>, 'children' | 'role' | 'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'>

/** The props every dropdown panel takes, whether it is the root menu's or a submenu's. */
export type DropdownPanelProps = PanelDivProps & {
    /**
     * If `true`, removes the default padding from the panel.
     */
    noPadding?: boolean
    /**
     * Skip the enter and exit animation. Defaults to the viewer's `prefers-reduced-motion` setting, so
     * pass this only to override that — an explicit `false` animates even for a viewer who asked not to.
     */
    reducedMotion?: boolean
    /**
     * Optional portal root for the panel. Defaults to the document body.
     */
    container?: HTMLElement | null
    /**
     * What the panel holds. `menu` is the default and is right for menu items. Pass `dialog` for anything
     * else — a date picker, a form — because a menu may only own menu items, and claiming otherwise is
     * what a screen reader will act on. A panel with a search field is a dialog whatever this says.
     */
    role?: 'menu' | 'dialog'
}

/** A panel holding a search field is a dialog around a menu, not a menu: see DropdownPanelBody. */
export const resolvePanelRole = (search: DropdownMenuSearch | undefined, role: 'menu' | 'dialog') => (search || role === 'dialog' ? 'dialog' : 'menu')

/** What a panel needs from the floating-ui instance that positions it. */
export type DropdownPanelFloating = {
    context: FloatingContext
    setFloating: (node: HTMLElement | null) => void
    /** The placement floating-ui settled on, which is not the requested one once the panel has flipped. */
    placement: Placement
    /**
     * Position as top/left rather than floating-ui's `floatingStyles`, which positions with a
     * `transform`. The panel animates its scale, and motion writes the whole `transform` property,
     * which would drop the translation and strand the panel in the corner of the viewport.
     */
    x: number
    y: number
    strategy: UseFloatingReturn['strategy']
    getFloatingProps: (userProps?: DropdownHtmlProps) => Record<string, unknown>
    getItemProps: (userProps: DropdownHtmlProps) => DropdownHtmlProps
}

type Props = Omit<DropdownPanelProps, 'reducedMotion'> & {
    open: boolean
    floating: DropdownPanelFloating
    list: DropdownList
    search: DropdownMenuSearch | undefined
    skipMotion: boolean
    closeMenu: () => void
    /** If `true`, focus is trapped inside the panel while it is open. */
    modal?: boolean
    /** A submenu's panel, which sits in the focus order of the menu it opened from rather than its own. */
    nested?: boolean
    children: ReactNode
}

/**
 * The floating panel of a root menu or a submenu: portalled, animated, and providing the menu context
 * its items register with. Rendering both through this is what keeps a nested panel indistinguishable
 * from the one it came out of.
 */
export const DropdownPanel = ({
    open,
    floating,
    list,
    search,
    skipMotion,
    closeMenu,
    modal = false,
    nested = false,
    noPadding,
    container,
    role = 'menu',
    className,
    children,
    ...contentProps
}: Props) => {
    const searchRef = useRef<HTMLInputElement>(null)
    const itemIdPrefix = useId()
    const [openSubmenu, setOpenSubmenuState] = useState<OpenSubmenu>(NO_SUBMENU)

    const setOpenSubmenu = (nodeId: string | null) => {
        // Returning the previous value keeps a row's every mouseenter from re-rendering a menu that
        // already has no submenu open.
        setOpenSubmenuState(previous => (previous.id === nodeId ? previous : { id: nodeId, handoff: nodeId !== null && previous.id !== null }))
    }

    // A closed panel has no open submenu. Clearing it as the panel closes is what lets a submenu animate
    // out alongside the menu it belongs to instead of being cut off when that menu finishes, and what
    // stops a submenu left open from reappearing the next time the menu is opened.
    const [wasOpen, setWasOpen] = useState(open)
    if (wasOpen !== open) {
        setWasOpen(open)
        if (!open) setOpenSubmenu(null)
    }

    // Held apart from this panel's own state, so a submenu opening does not by itself invalidate the
    // context its items read.
    const menuContext = {
        getItemProps: floating.getItemProps,
        closeMenu,
        skipMotion,
        activeIndex: search ? list.activeIndex : null,
        itemIdPrefix: search ? itemIdPrefix : undefined,
        focusSearch: search ? () => searchRef.current?.focus() : undefined,
        setOpenSubmenu,
    }

    return (
        // Outside the AnimatePresence, not within it. A panel on its way out keeps rendering the elements
        // AnimatePresence captured while it was open, and a provider captured with them would hand its
        // submenus the value they had then — leaving a submenu believing it is open, so it holds full
        // opacity and vanishes when this panel finishes instead of fading with it.
        <DropdownMenuContext value={menuContext}>
            <OpenSubmenuContext value={openSubmenu}>
                <AnimatePresence>
                    {open && (
                        <FloatingPortal
                            root={container}
                            // preserveTabOrder puts Floating UI's focus guards next to the trigger, which for a submenu
                            // means inside the parent panel — and a menu may only hold menu items. Arrow keys move
                            // around a menu anyway, so there is no tab order here worth preserving.
                            preserveTabOrder={!nested}
                        >
                            <FloatingFocusManager context={floating.context} modal={modal} initialFocus={search ? searchRef : -1} returnFocus={!nested}>
                                <motion.div
                                    {...contentProps}
                                    {...(skipMotion ? {} : FLOATING_MOTION_PROPS)}
                                    {...floating.getFloatingProps({
                                        ref: floating.setFloating,
                                        style: { ...contentProps.style, position: floating.strategy, top: floating.y, left: floating.x },
                                        className: astraTwMerge(
                                            DROPDOWN_PANEL_CLASS,
                                            TRANSFORM_ORIGIN_BY_PLACEMENT[floating.placement],
                                            search ? DROPDOWN_SEARCH_PANEL_CLASS : noPadding ? '' : DROPDOWN_PANEL_PADDING_CLASS,
                                            className
                                        ),
                                    })}
                                    role={resolvePanelRole(search, role)}
                                    // floating-ui's virtual navigation puts this on the panel, but the field is what has focus and it points at the row itself.
                                    aria-activedescendant={undefined}
                                >
                                    <FloatingList elementsRef={list.elementsRef} labelsRef={list.labelsRef}>
                                        <DropdownPanelBody search={search} searchRef={searchRef} list={list} itemIdPrefix={itemIdPrefix} noPadding={noPadding}>
                                            {children}
                                        </DropdownPanelBody>
                                    </FloatingList>
                                </motion.div>
                            </FloatingFocusManager>
                        </FloatingPortal>
                    )}
                </AnimatePresence>
            </OpenSubmenuContext>
        </DropdownMenuContext>
    )
}
